/**
 * OrgManager — API layer for all organization endpoints.
 *
 * All methods return parsed JSON or throw on non-2xx responses.
 */

import API_BASE_URL from '../config';
import ListsManager from './ListsManager';

const BASE_URL = API_BASE_URL;

async function getHeaders() {
  // Use ListsManager.getAuthToken() so we always get a valid Firebase ID token,
  // not the raw UID stored in AsyncStorage under 'userToken'.
  const token = await ListsManager.getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function request(method, path, body) {
  const headers = await getHeaders();
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${method} ${path} → ${res.status}: ${text}`);
  }
  return res.json();
}

const OrgManager = {
  // ── Org listing ────────────────────────────────────────────────────────────
  listMyOrgs: () => request('GET', '/orgs'),

  getOrg: (orgId) => request('GET', `/orgs/${orgId}`),

  updateOrg: (orgId, data) => request('PATCH', `/orgs/${orgId}`, data),

  deleteOrg: (orgId) => request('DELETE', `/orgs/${orgId}`),

  // ── Members ────────────────────────────────────────────────────────────────
  listMembers: (orgId) => request('GET', `/orgs/${orgId}/members`),

  changeMemberRole: (orgId, listenerId, role) =>
    request('PATCH', `/orgs/${orgId}/members/${listenerId}`, { role }),

  removeMember: (orgId, listenerId) =>
    request('DELETE', `/orgs/${orgId}/members/${listenerId}`),

  updateMemberReportFrequency: (orgId, listenerId, reportFrequency) =>
    request('PATCH', `/orgs/${orgId}/members/${listenerId}/report-frequency`, { report_frequency: reportFrequency }),

  updateMyPreferences: (orgId, data) =>
    request('PATCH', `/orgs/${orgId}/members/me/preferences`, data),

  // ── Invitations ────────────────────────────────────────────────────────────
  sendInvitation: (orgId, email, role = 'member') =>
    request('POST', `/orgs/${orgId}/invitations`, { email, role }),

  listInvitations: (orgId) => request('GET', `/orgs/${orgId}/invitations`),

  revokeInvitation: (orgId, inviteId) =>
    request('DELETE', `/orgs/${orgId}/invitations/${inviteId}`),

  /** No auth needed — token is in query string */
  previewInvitation: async (token) => {
    const res = await fetch(`${BASE_URL}/orgs/invitation-preview?token=${encodeURIComponent(token)}`);
    if (!res.ok) throw new Error(`Preview invite failed: ${res.status}`);
    return res.json();
  },

  acceptInvitation: (token) =>
    request('POST', '/orgs/accept-invitation', { token }),

  declineInvitation: (token) =>
    request('POST', '/orgs/decline-invitation', { token }),

  // ── Activity ────────────────────────────────────────────────────────────────
  getActivity: (orgId, sinceDays = 7) =>
    request('GET', `/orgs/${orgId}/activity?since_days=${sinceDays}`),

  // ── List access ────────────────────────────────────────────────────────────
  grantListAccess: (listId, listenerId, role = 'viewer') =>
    request('POST', `/lists/${listId}/access`, { listener_id: listenerId, role }),

  revokeListAccess: (listId, listenerId) =>
    request('DELETE', `/lists/${listId}/access/${listenerId}`),

  getListAccess: (listId) => request('GET', `/lists/${listId}/access`),

  // ── Campaign access ────────────────────────────────────────────────────────
  grantCampaignAccess: (campaignId, listenerId) =>
    request('POST', `/campaigns/${campaignId}/access`, { listener_id: listenerId }),

  revokeCampaignAccess: (campaignId, listenerId) =>
    request('DELETE', `/campaigns/${campaignId}/access/${listenerId}`),

  getCampaignAccess: (campaignId) => request('GET', `/campaigns/${campaignId}/access`),

  // ── Org X accounts ─────────────────────────────────────────────────────────
  listOrgXAccounts: (orgId) =>
    request('GET', `/auth/twitter/org/${orgId}/accounts`),

  disconnectOrgXAccount: (orgId, xUserId) =>
    request('DELETE', `/auth/twitter/org/${orgId}/accounts/${xUserId}`),

  // ── Escalation Config ────────────────────────────────────────────────────────
  updateEscalationConfig: (orgId, config) =>
    request('PATCH', `/orgs/${orgId}`, { escalation_config: config }),

  // ── Tags ────────────────────────────────────────────────────────────────────
  listTags: (orgId) => request('GET', `/orgs/${orgId}/tags`),
  createTag: (orgId, name, description) => request('POST', `/orgs/${orgId}/tags`, { name, description }),
  updateTag: (orgId, tagId, data) => request('PUT', `/orgs/${orgId}/tags/${tagId}`, data),
  deleteTag: (orgId, tagId) => request('DELETE', `/orgs/${orgId}/tags/${tagId}`),

  // ── Competitors ────────────────────────────────────────────────────────────
  listCompetitors: (orgId) => request('GET', `/orgs/${orgId}/competitors`),
  createCompetitor: (orgId, data) => request('POST', `/orgs/${orgId}/competitors`, data),
  updateCompetitor: (orgId, competitorId, data) => request('PUT', `/orgs/${orgId}/competitors/${competitorId}`, data),
  deleteCompetitor: (orgId, competitorId) => request('DELETE', `/orgs/${orgId}/competitors/${competitorId}`),

  // ── TV Watch Terms (live-TV keyword monitoring) ─────────────────────────────
  listTvWatchTerms: (orgId) => request('GET', `/orgs/${orgId}/tv-watch-terms`),
  createTvWatchTerm: (orgId, data) => request('POST', `/orgs/${orgId}/tv-watch-terms`, data),
  updateTvWatchTerm: (orgId, termId, data) => request('PATCH', `/orgs/${orgId}/tv-watch-terms/${termId}`, data),
  deleteTvWatchTerm: (orgId, termId) => request('DELETE', `/orgs/${orgId}/tv-watch-terms/${termId}`),
  bulkDeleteTvWatchTerms: (orgId, ids) => request('POST', `/orgs/${orgId}/tv-watch-terms/bulk-delete`, { ids }),

  // ── Keyword Alerts ────────────────────────────────────────────────────────
  listKeywordAlerts: (orgId) => request('GET', `/orgs/${orgId}/keyword-alerts`),
  createKeywordAlert: (orgId, data) => request('POST', `/orgs/${orgId}/keyword-alerts`, data),
  updateKeywordAlert: (orgId, alertId, data) => request('PUT', `/orgs/${orgId}/keyword-alerts/${alertId}`, data),
  deleteKeywordAlert: (orgId, alertId) => request('DELETE', `/orgs/${orgId}/keyword-alerts/${alertId}`),

  // ── Admin (platform super-admin only) ──────────────────────────────────────
  adminCreateOrg: (name, slug, firstOwnerListenerId, plan = 'free') =>
    request('POST', '/admin/orgs', { name, slug, first_owner_listener_id: firstOwnerListenerId, plan }),

  adminListOrgs: (search) =>
    request('GET', `/admin/orgs${search ? `?search=${encodeURIComponent(search)}` : ''}`),

  adminGetOrg: (orgId) => request('GET', `/admin/orgs/${orgId}`),

  adminUpdateOrg: (orgId, data) => request('PATCH', `/admin/orgs/${orgId}`, data),

  adminDeleteOrg: (orgId) => request('DELETE', `/admin/orgs/${orgId}`),

  adminAddMember: (orgId, listenerId, role = 'member') =>
    request('POST', `/admin/orgs/${orgId}/members`, { listener_id: listenerId, role }),

  adminRemoveMember: (orgId, listenerId) =>
    request('DELETE', `/admin/orgs/${orgId}/members/${listenerId}`),

  adminChangeMemberRole: (orgId, listenerId, role) =>
    request('PATCH', `/admin/orgs/${orgId}/members/${listenerId}/role`, { role }),

  adminGetListenerOrgs: (listenerUuid) =>
    request('GET', `/admin/listeners/${listenerUuid}/orgs`),

  adminBulkAddListenerToOrgs: (listenerUuid, orgIds, role = 'member') =>
    request('POST', `/admin/listeners/${listenerUuid}/orgs`, { org_ids: orgIds, role }),
};

export default OrgManager;
